import React, {Fragment, useState} from "react";
import {makeStyles} from "tss-react/mui";

// @mui/icons-material
// core components
import Button from "../../components/CustomButtons/Button.js";
import Card from "../../components/Card/Card.js";
import CardFooter from "../../components/Card/CardFooter.js";
import JobsSidebar from "../JobsSidebar";
import EmployerJobsSidebar from "../EmployerJobsSidebar";
import {SanitizeHTML} from "../../util/string";
import {buildSlug, getHostname, updateQueryString} from "../../util/url";

import styles from "../../styles/jss/nextjs-material-kit/pages/landingPageSections/TheDetailsStyle.js";
import overviewStyles from "../../styles/jss/nextjs-material-kit/pages/componentsSections/jobOverviewStyle.js";
import Grid from "@mui/material/Grid";
import Link from "next/link";
import {ROLE_SCHOOL_USER_ID} from "../../util/roles";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import {useEmployersVisitRequest, useFavouritesAddRequest, useStudentFavouritesListRequest,} from "../../request/api";
import {useAuthContext} from "../../contexts/auth";
import EmployerForumPanel from "../../components/EmployerForumPanel/EmployerForumPanel.js";
import {ForumProvider} from "../../contexts/forum";
import {useRouter} from "next/router";
import {InviteEmployerDialog} from "./InviteEmployerDialog";

const useStyles = makeStyles()(styles);
const useOverviewStyles = makeStyles()(overviewStyles);

/**
 * Shows details of employer or university.
 */
export default function OrganisationDetails({ org, orgDetails, isUniversity = false }) {
  const { classes, cx } = useStyles();
  const { classes: overviewClasses } = useOverviewStyles();
  const { trigger: trackVisit } = useEmployersVisitRequest({ employerId: org?.id });

  const {
    isMutating: isUpdatingFavourites,
    trigger: addToFavourites,
    error: favouritesError,
  } = useFavouritesAddRequest(
    { type: org.type, entityId: org?.id },
    undefined,
    { withCredentials: true }
  );
  const { auth } = useAuthContext();
  const router = useRouter();
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);

  const handleClickVisit = async (event) => {
    await trackVisit();
    window.gtag && window.gtag("event", "employer_profile_visit", {
      org_id: org?.id,
    });
  };

  const {
    data: favouritesData,
    error: favouritesErrors,
    isLoading: isLoadingFavourites,
    mutate: updateFavourites,
  } = useStudentFavouritesListRequest(
    {
      type: org.type,
    },
    {
      shouldFetch: !!auth.user,
    },
    { withCredentials: true }
  );

  const isFavourite = favouritesData
    ?.map((favourite) => favourite.entityId)
    .includes(org.id);
  const handleFavouriteToggle = async (event) => {
    if (!auth.user) {
      router.push(updateQueryString(updateQueryString(router.asPath, "login", null), "redirectUrl", router.asPath));
    }
    else {
      await addToFavourites();

      await updateFavourites();
    }
  };

  const handleInviteClick = () => {
    setInviteDialogOpen(true);
  };

  const handleInviteDialogClose = () => {
    setInviteDialogOpen(false);
  };

  return (
    <>
      <div
        className={cx(
          classes.marginAuto,
          classes.bg_colored,
          classes.marginBelow
        )}
      >
        <div className={classes.relativeContainer}>
          <div className={cx(classes.employerLogoOuterContainer)}>
            <div className={cx(classes.employerLogoInnerContainer)}>
              <div
                className={cx(classes.employerLogo)}
                style={{
                  backgroundImage: `url(${org.logo_uri})`,
                }}
              ></div>
            </div>
          </div>
        </div>

        <Card
          className={cx(overviewClasses.jobCard, classes.cardArea, "jobcard")}
        >
          <div
            className={cx(
              classes.container,
              overviewClasses.floatLeft,
              classes.titles
            )}
          >
            <h1 className={overviewClasses.generalTitle}>
              {org.name}

              <div className={cx(classes.floatRight, classes.favouriteButton)}>
                {!auth.user || auth.user?.role === ROLE_SCHOOL_USER_ID ? (
                  <>
                    <span onClick={handleFavouriteToggle}>
                      {isFavourite ? (
                        <>
                          <FavoriteIcon
                            height="16px"
                            className={cx(classes.icons, classes.jobIcons)}
                          />
                        </>
                      ) : (
                        <>
                          <FavoriteBorderIcon
                            height="16px"
                            className={cx(classes.icons, classes.jobIcons)}
                          />
                        </>
                      )}
                    </span>
                  </>
                ) : null}
              </div>
            </h1>

            {org.uri && (
              <Button
                className={cx(classes.homeSearchButton, "button")}
                component="a"
                href={org.uri}
                rel="noopener external"
                target="_blank"
                onClick={handleClickVisit}
              >
                Visit {org.type} website
              </Button>
            )}

            {orgDetails?.forumCategoryActive && (
                <Button
                  className={cx(classes.homeSearchButton, classes.communityBtn, "button")}
                  component="a"
                  href={`/community/${buildSlug(org.name)}-${orgDetails.forumCategoryId}`}
                >
                  Go to the {org.name} Community
                </Button>
            )}

            {/* Disabled for now: */}
            {/*{auth.user?.role === ROLE_SCHOOL_ADMIN_ID && org.type === "employer" && (*/}
            {/*  <Button*/}
            {/*    className={cx(classes.homeSearchButton, "button")}*/}
            {/*    onClick={handleInviteClick}*/}
            {/*  >*/}
            {/*    Invite employer to visit your school*/}
            {/*  </Button>*/}
            {/*)}*/}

            <h4 className={overviewClasses.cardSubTitle}>
              {org.description_short}
            </h4>
          </div>
          <CardFooter
            className={cx(
              classes.container,
              classes.leftAlign,
              classes.cardFooter
            )}
          >
            <Grid container>
              <Grid item xs={12} sm={12} md={8}>
                {" "}
                {/* Details except website */}
                <div className={overviewClasses.small}>
                  <strong>Established</strong>:{" "}
                  <span>{org.established_year || "Unknown"}</span>
                </div>
                {org.employee_count > 0 ? (
                  <div className={overviewClasses.small}>
                    <strong>No. of employees</strong>:{" "}
                    <span>{org.employee_count.toLocaleString()}</span>
                  </div>
                ) : null}
                {org.career_zones.length ? (
                  <div className={overviewClasses.small}>
                    <strong>Career zones</strong>:{" "}
                    <span>
                      {org.career_zones.map((zone, i) => (
                        <Fragment key={i}>
                          <Link
                            href={`/career-zone/${buildSlug(zone.name)}/${
                              zone.id
                            }`}
                          >
                            {zone.name}
                          </Link>
                          {i < org.career_zones.length - 1 ? ", " : ""}
                        </Fragment>
                      ))}
                    </span>
                  </div>
                ) : null}
                <div className={overviewClasses.small}>
                  <strong>Location</strong>:{" "}
                  <span>
                    {org.locations?.length > 0
                      ? org.locations
                          .map((location) => location.name)
                          .join(", ")
                      : "N/A"}
                  </span>
                </div>
              </Grid>

              {org.uri && (
                <Grid item xs={12} sm={12} md={4}>
                  <div className={overviewClasses.small}>
                    <strong>Website</strong>:{" "}
                    <span>
                      <a href={org.uri} target="_blank" rel="noopener external">
                        {org.website_title || getHostname(org.uri)}
                      </a>
                    </span>
                  </div>
                </Grid>
              )}
            </Grid>
          </CardFooter>
        </Card>
      </div>

      <div className={classes.container}>
        <Grid container justifyContent="space-between">
          <Grid
            item
            className={cx(classes.floatLeft, classes.mainContent)}
            xs={12}
            sm={12}
            md={8}
          >
            {org?.details?.length > 0 &&
              org.details.map((detail, i) => (
                <Fragment key={i}>
                  <h2 className={classes.cardTitle}>{detail.title}</h2>
                  <div className={classes.description}>
                    <SanitizeHTML
                      html={detail.content}
                      className="user-content"
                    />
                  </div>
                </Fragment>
              ))}
          </Grid>

          <Grid item className={classes.paddingZero} xs={12} sm={12} md={3}>
            {orgDetails?.forumCategoryActive && org.type === "employer" && (
              <ForumProvider forumId={orgDetails.forumCategoryId}>
                <EmployerForumPanel
                  employerId={org.id}
                  employerName={org.name}
                  forumId={orgDetails.forumCategoryId}
                />
              </ForumProvider>
            )}
            {org?.type === "employer" ? (
              <EmployerJobsSidebar
                headingText={`${org.name} jobs`}
                org={org}
              />
            ) : (
              <JobsSidebar
                headingText={false}
                org={org}
                isUniversity={isUniversity}
              />
            )}
          </Grid>
        </Grid>
      </div>

      {inviteDialogOpen && (
        <InviteEmployerDialog
          open={inviteDialogOpen}
          onClose={handleInviteDialogClose}
          org={org}
          auth={auth}
          trackVisit={trackVisit}
        />
      )}
    </>
  );
}
